/**
 * FetchMemResult 组件
 * 用于显示 FETCH_MEM 工具的查询结果，包括查询统计信息
 */
import React from 'react';

import { ProcessStep, isFetchMemStep } from '../../../../../types';
import { FiSearch, FiCalendar, FiCheckCircle, FiCircle, FiClock } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface FetchMemResultProps {
  step: ProcessStep;
}

export const FetchMemResult: React.FC<FetchMemResultProps> = ({ step }) => {

  const { t } = useTranslation(['v3/chat', 'common']);
  
  // 类型守卫：确保是FETCH_MEM工具结果
  if (!isFetchMemStep(step)) {
    return null;
  }

  // 解析工具结果数据
  const parseToolResult = () => {
    try {
      if (!step.result) {
        return null;
      }

      const result = step.result;

      // 直接使用类型化的结果
      return {
        totalFound: result.totalFound || 0,
        hasCompleted: result.hasCompleted || 0,
        hasPending: result.hasPending || 0,
        timeRange: result.timeRange || null,
        message: result.message || '',
        results: result.results || []
      };
    } catch (error) {
      console.error('解析 FETCH_MEM 结果失败:', error);
      return null;
    }
  };

  const resultData = parseToolResult();

  if (!resultData) {
    return null;
  }

  const { totalFound, hasCompleted, hasPending, timeRange, message } = resultData;

  // 格式化时间范围显示
  const formatTimeRange = (timeRange: any) => {
    if (!timeRange) return null;
    
    const { start, end } = timeRange;
    if (start && end) {
      return `${new Date(start).toLocaleDateString()} - ${new Date(end).toLocaleDateString()}`;
    } else if (start) {
      return `${new Date(start).toLocaleDateString()} 之后`;
    } else if (end) {
      return `${new Date(end).toLocaleDateString()} 之前`;
    }
    return null;
  };

  return (
    <div className="border border-ios-gray5 dark:border-ios-dark-gray4 rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
      {/* 查询结果标题 */}
      <div className="flex items-center gap-2 mb-3">
        <FiSearch className="w-4 h-4 text-green-600 dark:text-green-400" />
        <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
          {t('processSteps.queryResults', '查询结果')}
        </h4>
      </div>

      {/* 统计信息 */}
      <div className="space-y-2">
        {/* 总数统计 */}
        <div className="flex items-center gap-2 text-sm">
          <FiCircle className="w-3 h-3 text-gray-500" />
          <span className="text-gray-700 dark:text-gray-300">
            {t('processSteps.totalFound', '共找到 {{count}} 个任务', { count: totalFound })}
          </span>
        </div>

        {/* 完成状态统计 */}
        {(hasCompleted > 0 || hasPending > 0) && (
          <div className="flex items-center gap-4 text-sm">
            {hasCompleted > 0 && (
              <div className="flex items-center gap-1">
                <FiCheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  {t('processSteps.completed', '已完成: {{count}}', { count: hasCompleted })}
                </span>
              </div>
            )}
            {hasPending > 0 && (
              <div className="flex items-center gap-1">
                <FiCircle className="w-3 h-3 text-orange-500" />
                <span className="text-gray-700 dark:text-gray-300">
                  {t('processSteps.pending', '未完成: {{count}}', { count: hasPending })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* 时间范围 */}
        {timeRange && formatTimeRange(timeRange) && (
          <div className="flex items-center gap-2 text-sm">
            <FiCalendar className="w-3 h-3 text-blue-500" />
            <span className="text-gray-700 dark:text-gray-300">
              {t('processSteps.timeRange', '时间范围: {{range}}', { range: formatTimeRange(timeRange) })}
            </span>
          </div>
        )}

        {/* 查询时间 */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <FiClock className="w-3 h-3" />
          <span>{new Date(step.timestamp).toLocaleString()}</span>
        </div>

        {/* 消息 */}
        {message && (
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default FetchMemResult;
